import { useEffect, useRef, useState } from "react";
import { Pause, Play, ScanLine } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { copyFor } from "@shared/bilingualContent";

const VIDEO_MP4 = "/manus-storage/qm-workflow-film_23d767a1.mp4";
const VIDEO_WEBM = "/manus-storage/qm-workflow-film_49e49f00.webm";
const POSTER = "/manus-storage/qm-workflow-poster_ed70b3dc.jpg";

export function CinematicWorkflow() {
  const { locale } = useLocale();
  const copy = copyFor("video", locale);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [mobileFallback, setMobileFallback] = useState(false);
  const [paused, setPaused] = useState(true);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mobile = window.matchMedia("(max-width: 767px)");
    const sync = () => {
      setReducedMotion(motion.matches);
      setMobileFallback(mobile.matches);
    };
    sync();
    motion.addEventListener("change", sync);
    mobile.addEventListener("change", sync);
    return () => {
      motion.removeEventListener("change", sync);
      mobile.removeEventListener("change", sync);
    };
  }, []);

  useEffect(() => {
    if (reducedMotion || mobileFallback) return;
    const update = () => {
      frameRef.current = null;
      const video = videoRef.current;
      const section = video?.closest("[data-cinematic-workflow]");
      if (!video || !section || !video.duration) return;
      const rect = section.getBoundingClientRect();
      const travel = Math.max(1, rect.height - window.innerHeight);
      const progress = Math.max(0, Math.min(1, -rect.top / travel));
      video.currentTime = progress * video.duration;
    };
    const onScroll = () => {
      if (frameRef.current === null) frameRef.current = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [reducedMotion, mobileFallback]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video || reducedMotion || mobileFallback) return;
    if (video.paused) {
      void video.play();
      setPaused(false);
    } else {
      video.pause();
      setPaused(true);
    }
  };

  const showPoster = reducedMotion || mobileFallback || videoError;
  return (
    <div data-cinematic-workflow className="cinematic-workflow grid gap-8 lg:grid-cols-[1.25fr_.75fr] lg:items-start">
      <div className="cinematic-workflow-media lg:sticky lg:top-28">
        <div className="media-frame relative overflow-hidden bg-[#080a10]" aria-label={copy.aria}>
          {showPoster ? (
            <img src={POSTER} alt={copy.posterAlt} className="block aspect-video h-auto w-full object-cover" loading="lazy" />
          ) : (
            <video ref={videoRef} className="block aspect-video h-auto w-full object-cover" poster={POSTER} muted playsInline preload="metadata" aria-label={copy.aria} onError={() => setVideoError(true)}>
              <source src={VIDEO_WEBM} type="video/webm" />
              <source src={VIDEO_MP4} type="video/mp4" />
            </video>
          )}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#080a10]/70 via-transparent to-transparent" />
          <div className="absolute inset-x-5 bottom-5 flex items-center justify-between gap-4 border-t border-white/15 pt-4">
            <span className="mono-label text-white/80"><ScanLine className="me-2 inline size-3" />{copy.workflow}</span>
            {!showPoster && <button type="button" className="button-secondary px-3 py-2 text-xs" onClick={togglePlayback} aria-label={paused ? copy.play : copy.pause}>{paused ? <Play className="size-3" /> : <Pause className="size-3" />}{paused ? copy.play : copy.pause}</button>}
          </div>
        </div>
          <p className="mt-3 text-xs leading-5 text-slate-500">{videoError ? copy.fallback : copy.note}</p>
      </div>
      <div className="grid gap-3 lg:pt-[10vh]">
        {copy.steps.map((step, index) => (
          <div key={step.label} className="border-s-2 border-white/10 bg-white/[.025] px-5 py-5 transition-colors hover:border-indigo-200/70 hover:bg-indigo-200/[.04]">
            <span className="mono-label text-indigo-200">0{index + 1} · {step.label}</span>
            <p className="mt-3 text-lg font-medium text-white">{step.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
